import type { RequestException } from '@eika-infrastruktur/request';
import type { FundSavingsCalculationResults } from '@generated-types';
import { useInputs } from '@hooks/use-inputs';
import { getCalculatedResults } from '@react-query/services/calculated-results';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from './use-debounced-value';

export const useCalculatedResults = () => {
    const { calcInputs } = useInputs();

    // Debounce to avoid sending requests too frequently on fast input changes
    const debouncedCalcInputs = useDebouncedValue(calcInputs, 500);

    const normalizedCalcInputs = {
        ...debouncedCalcInputs,
        initialInvestment: debouncedCalcInputs.initialInvestment ?? 0,
        monthlyContribution: debouncedCalcInputs.monthlyContribution ?? 0,
        savingPeriodYears: debouncedCalcInputs.savingPeriodYears ?? 0,
    };

    return useQuery<FundSavingsCalculationResults, RequestException>({
        queryKey: [
            'calculated-results',
            normalizedCalcInputs.initialInvestment,
            normalizedCalcInputs.monthlyContribution,
            normalizedCalcInputs.savingPeriodYears,
        ],
        queryFn: () => getCalculatedResults(normalizedCalcInputs),
        // Use previous data while loading to prevent a flicker to undefined on fast input changes
        placeholderData: (previousData) => previousData,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });
};
